import { createApi, createBaseQuery } from '@adv-frontend/modules-query';
import { PROMOS_LIST_GLOBAL_TAG } from '#shared/api/globalTags';
import { apiV1Request } from '#shared/api/requests';
import {
    type OpenApiGetPromosResponseSuccess,
    type OpenApiGetPromosSearchParams,
    type OpenApiGetPromosTimelineResponseSuccess,
    type OpenApiGetPromosTimelineSearchParams,
    OpenApiGetPromosTimelineUrl,
    OpenApiGetPromosUrl,
    type OpenApiSchemaPromo,
} from '#shared/api/schema';
import { InvalidateGridApiHelper } from '#shared/lib/requests';
import { encodeSearchParams } from '#shared/lib/url';

export const PromosTag = 'Promos' as const;

export const promosGridTagInvalidator = new InvalidateGridApiHelper<typeof PromosTag, OpenApiGetPromosSearchParams>(
    PromosTag,
    'promosGrid'
);

/** Аргументы закешированных getList — чтобы обходить все ответы при точечном апдейте */
const promosListArgsByKey = new Map<string, OpenApiGetPromosSearchParams>();
/** Аргументы закешированных getTimeline */
const promosTimelineArgsByKey = new Map<string, OpenApiGetPromosTimelineSearchParams>();

/**
 * Сохраняет аргументы query для последующего обхода кеша.
 *
 * @typeParam T Тип аргументов query.
 * @param cache Map аргументов.
 * @param args Аргументы запроса.
 * @kind helper
 */
function rememberQueryArgs<T>(cache: Map<string, T>, args: T) {
    try {
        cache.set(JSON.stringify(args), args);
    } catch {
        // ignore non-serializable args
    }
}

type PromoUpdateResult = OpenApiSchemaPromo | null;

function updatePromoInCachedResponses(onPromoUpdate: (promo: OpenApiSchemaPromo) => PromoUpdateResult) {
    for (const args of promosListArgsByKey.values()) {
        promosApi.endpoints.getList.updateQueryData(args, (draft) => {
            if (!draft) {
                return;
            }

            const nextPromos: OpenApiSchemaPromo[] = [];
            let removedCount = 0;

            for (const promo of draft.promos) {
                const updatedPromo = onPromoUpdate(promo);

                if (updatedPromo) {
                    nextPromos.push(updatedPromo);
                } else {
                    removedCount += 1;
                }
            }

            draft.promos = nextPromos;

            if (removedCount > 0) {
                draft.total_count = Math.max(0, draft.total_count - removedCount);
            }
        });
    }

    for (const args of promosTimelineArgsByKey.values()) {
        promosApi.endpoints.getTimeline.updateQueryData(args, (draft) => {
            if (!draft) {
                return;
            }

            const nextPromos: OpenApiSchemaPromo[] = [];

            for (const promo of draft) {
                const updatedPromo = onPromoUpdate(promo);

                if (updatedPromo) {
                    nextPromos.push(updatedPromo);
                }
            }

            return nextPromos;
        });
    }
}

/**
 * Удаляет акцию из всех закешированных ответов getList/getTimeline.
 *
 * @param promoId Id акции.
 * @kind helper
 */
export function removePromoFromPromosCache(promoId: string) {
    updatePromoInCachedResponses((promo) => (promo.id === promoId ? null : promo));
}

/**
 * Обновляет is_currently_active в getList/getTimeline, если значение изменилось.
 *
 * @param promoId Id акции.
 * @param isCurrentlyActive Новое значение is_currently_active.
 * @kind helper
 */
export function updatePromoIsCurrentlyActiveInPromosCache(promoId: string, isCurrentlyActive: boolean) {
    updatePromoInCachedResponses((promo) => {
        if (promo.id !== promoId || promo.is_currently_active === isCurrentlyActive) {
            return promo;
        }

        return { ...promo, is_currently_active: isCurrentlyActive };
    });
}

/**
 * Синхронизирует status и is_currently_active в getList/getTimeline после activate/deactivate.
 *
 * @param promoId Id акции.
 * @param fields Новые status и is_currently_active.
 * @kind helper
 */
export function updatePromoActivationInPromosCache(
    promoId: string,
    fields: Pick<OpenApiSchemaPromo, 'is_currently_active' | 'status'>
) {
    updatePromoInCachedResponses((promo) => {
        if (promo.id !== promoId) {
            return promo;
        }

        if (promo.is_currently_active === fields.is_currently_active && promo.status === fields.status) {
            return promo;
        }

        return { ...promo, ...fields };
    });
}

export const promosApi = createApi({
    name: 'entities/discount/api/promosApi',
    tagTypes: [PromosTag, PROMOS_LIST_GLOBAL_TAG],
    baseQuery: createBaseQuery(apiV1Request),
    endpoints: (builder) => {
        return {
            getList: builder.query<OpenApiGetPromosResponseSuccess, OpenApiGetPromosSearchParams>({
                providesTags: (_result, error, args) => {
                    if (error) {
                        return [];
                    }

                    rememberQueryArgs(promosListArgsByKey, args);

                    return [
                        { type: PromosTag, id: 'promosGrid' },
                        PROMOS_LIST_GLOBAL_TAG,
                        ...promosGridTagInvalidator.provideHashTagForGridEndpoint(args),
                    ];
                },
                query: (params) => {
                    return {
                        url: `${OpenApiGetPromosUrl}${encodeSearchParams(params ?? {})}`,
                        method: 'GET',
                    };
                },
            }),
            getTimeline: builder.query<OpenApiGetPromosTimelineResponseSuccess, OpenApiGetPromosTimelineSearchParams>({
                providesTags: (_result, error, args) => {
                    if (error) {
                        return [];
                    }

                    rememberQueryArgs(promosTimelineArgsByKey, args);

                    return [{ type: PromosTag, id: 'promosTimeline' }, PROMOS_LIST_GLOBAL_TAG];
                },
                query: (params) => {
                    return {
                        url: `${OpenApiGetPromosTimelineUrl}${encodeSearchParams(params)}`,
                        method: 'GET',
                    };
                },
            }),
        };
    },
});

export const useDiscountPromosTimeline = promosApi.useGetTimelineQuery;

export const useDiscountPromosLazyList = promosApi.useLazyGetListQuery;
